// Tables · DocumentPill
//
// One pill for every document context. Unifies the three shapes the
// transaction docs bucket renders — uploaded doc-type pills (worst-wins
// review tone + ×count), aggregated photo pills (per-status tally), and
// generated-doc slots (present/missing + regenerate) — behind a single
// primitive so callers stop re-deriving tone maps and status counts per file.
//
// The CALLER owns the click destination (`onOpen`) — DocumentPill never
// decides between "open review modal" and "open the Drive doc". A secondary
// action (regenerate / upload) can sit to the right of the label without
// firing `onOpen` (it stops propagation).
//
// Bespoke leaf chrome (asymmetric label padding + hover-toggleable
// secondary-action divider) mirrors the sibling `DocStatusPill` — no closed
// Pill primitive carries a stacked secondary-action slot. Tone mapping is
// unified here via `reviewTone` + `state`.
//
// All visual values come from `@ds/tokens/palette`.

import React, { useState, type ReactNode } from "react";
import { FONT_SIZE, RADIUS, SPACE, TONE, type ToneName } from "@ds/tokens/palette";
import { type ReviewTone, type DocumentPillState } from "@ds/tables/cells/DocumentPill.helpers";

export type { ReviewTone, DocumentPillState } from "@ds/tables/cells/DocumentPill.helpers";

// Review tone → palette tone. The one place the mapping lives; unifies the
// green/amber/red/grey selection previously inlined in DocStatusPill:91-120.
const REVIEW_TONE_TO_PALETTE: Record<ReviewTone, ToneName> = {
  not_approved: "red",
  pending:      "amber",
  approved:     "green",
  none:         "grey",
};

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface DocumentPillTally {
  approved: number;
  pending: number;
  rejected: number;
}

export interface DocumentPillSecondaryAction {
  /** Which affordance the trailing button represents. */
  kind: "regenerate" | "upload" | "resend" | "download";
  icon: ReactNode;
  onClick: () => void;
  title: string;
  testId?: string;
}

export interface DocumentPillInput {
  /** The pill label — a resolved document-type label (e.g. "CMR", "Aneks VII"). */
  label: string;
  /** Leading icon node (e.g. an eye / camera). */
  icon?: ReactNode;
  /** Presence / lifecycle state. Drives grey vs review-toned chrome. */
  state: DocumentPillState;
  /** Worst-wins review tone across the pill's document(s). */
  reviewTone: ReviewTone;
  /** `×N` badge for multiple files of one type. Omitted / ≤1 → no badge. */
  count?: number;
  /**
   * Photos-mode per-status breakdown. When present the label is replaced by
   * the "✓N ⏳N ✗N" counts (only non-zero segments render).
   */
  tally?: DocumentPillTally;
  /** `data-testid` on the root pill element. */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────────

export interface DocumentPillOutput {
  /** Primary click target. The caller decides the destination. */
  onOpen?: () => void;
}

// ─── Side actions ─────────────────────────────────────────────────────────────

export interface DocumentPillActions {
  /**
   * Trailing affordance (regenerate / upload). Stops propagation so `onOpen`
   * does not fire when it is clicked.
   */
  secondaryAction?: DocumentPillSecondaryAction;
}

export type DocumentPillProps = DocumentPillInput & DocumentPillOutput & DocumentPillActions;

// ─── Chrome resolution ────────────────────────────────────────────────────────

interface Chrome {
  bg: string;
  hoverBg: string;
  border: string;
  color: string;
  divider: string;
}

function resolveChrome(state: DocumentPillState, reviewTone: ReviewTone): Chrome {
  // Missing / unmatched / generating always read as neutral grey — there is no
  // document (or no match) to carry a review tone yet.
  const tone: ToneName =
    state === "present" ? REVIEW_TONE_TO_PALETTE[reviewTone] : "grey";
  const t = TONE[tone];
  // Grey uses the deeper `soft` fill (matches DocStatusPill missing states);
  // toned states use the lighter `softBg` wash so text stays legible.
  return {
    bg:      tone === "grey" ? t.soft : t.softBg,
    hoverBg: t.border,
    border:  t.border,
    color:   t.text,
    divider: t.border,
  };
}

// ─── Tally rendering ──────────────────────────────────────────────────────────

function TallySegments({
  tally,
  fallback,
}: {
  tally: DocumentPillTally;
  fallback: ReactNode;
}): React.ReactElement {
  const segments: React.ReactNode[] = [];
  if (tally.approved > 0) segments.push(<span key="a">✓ {tally.approved}</span>);
  if (tally.pending > 0)  segments.push(<span key="p">⏳ {tally.pending}</span>);
  if (tally.rejected > 0) segments.push(<span key="r">✗ {tally.rejected}</span>);
  return (
    <>
      {segments.length > 0
        ? segments.reduce<React.ReactNode[]>(
            (acc, seg, i) => (i > 0 ? [...acc, " ", seg] : [seg]),
            [],
          )
        : fallback}
    </>
  );
}

// Hover-aware trailing button — mirrors DocStatusPill's SecondaryAction: on
// hover deepens to the divider color to make the click target unambiguous.
function SecondaryButton({
  action,
  dividerColor,
}: {
  action: DocumentPillSecondaryAction;
  dividerColor: string;
}): React.ReactElement {
  const [hover, setHover] = useState(false);
  return (
    <button
      title={action.title}
      data-testid={action.testId}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); action.onClick(); }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        // 6px horizontal padding aligns with icon-only content (matches DocStatusPill).
        padding: "0 6px",
        background: hover ? dividerColor : "transparent",
        color: "inherit",
        opacity: hover ? 1 : 0.75,
        cursor: "pointer",
        border: "none",
        borderLeftWidth: 1,
        borderLeftStyle: "solid",
        borderLeftColor: dividerColor,
        outline: "none",
        transition: "background 100ms ease, opacity 100ms ease",
      }}
    >
      {action.icon}
    </button>
  );
}

// ─── Implementation ────────────────────────────────────────────────────────────

export function DocumentPill({
  label,
  icon,
  state,
  reviewTone,
  count,
  tally,
  testId,
  onOpen,
  secondaryAction,
}: DocumentPillProps): React.ReactElement {
  const [labelHover, setLabelHover] = useState(false);
  const chrome = resolveChrome(state, reviewTone);
  const showCount = typeof count === "number" && count > 1;

  return (
    <span
      role="group"
      data-testid={testId}
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        height: 20,
        borderRadius: RADIUS.pill,
        overflow: "hidden",
        fontSize: FONT_SIZE.label,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        background: chrome.bg,
        border: `1px solid ${chrome.border}`,
        color: chrome.color,
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
        onMouseEnter={onOpen ? () => setLabelHover(true)  : undefined}
        onMouseLeave={onOpen ? () => setLabelHover(false) : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: SPACE.xs, // 4px — icon–label gap in dense pill context
          padding: secondaryAction ? "1px 6px 1px 7px" : "1px 7px",
          background: onOpen && labelHover ? chrome.hoverBg : "transparent",
          border: "none",
          color: "inherit",
          fontSize: "inherit",
          fontFamily: "inherit",
          cursor: onOpen ? "pointer" : "default",
          outline: "none",
          transition: "background 100ms ease",
        }}
      >
        {icon && (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {icon}
          </span>
        )}
        {tally ? <TallySegments tally={tally} fallback={label} /> : `${label}${showCount ? ` ×${count}` : ""}`}
      </button>
      {secondaryAction && (
        <SecondaryButton action={secondaryAction} dividerColor={chrome.divider} />
      )}
    </span>
  );
}
